import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserAccessPermissions } from '../../../redux/UserManagement/actions';

export const useUserPermissionsAfterLogin = () => {
  const dispatch = useDispatch();
  
  // Select user permissions state from Redux store
  const userPermissions = useSelector((state: any) => state.userManagement?.userPermissions.data);
  // Get current user from auth state
  const currentUser = useSelector((state: any) => state.auth?.user);

  // Fetch user permissions after login
  const fetchUserPermissions = useCallback((cognitoId: string) => {
    if (cognitoId) {
      dispatch(getUserAccessPermissions(cognitoId));
    }
  }, [dispatch]);

  // // Auto-fetch permissions when user logs in
  // useEffect(() => {
  //   //   currentUser: currentUser?.cognitoId,
  //   //   userPermissions: userPermissions?.data,
  //   //   loading: userPermissions?.loading
  //   // });
    
  //   if (currentUser?.sub && !userPermissions?.data) {
  //     fetchUserPermissions(currentUser.sub);
  //   } else if (userPermissions?.data) {
  //   } else if (!currentUser?.sub) {
  //   }
  // }, [currentUser?.sub, userPermissions?.data, fetchUserPermissions]);

  // Get the appLevelRole object from the API response
  const getAppLevelRole = useCallback(() => {
    
    if (!userPermissions?.data) {
      // if(currentUser?.sub) {
      //   fetchUserPermissions(currentUser.sub);
      
      // }
      return null;
    }
    
    // Check if the data has appLevelRole property
    if (userPermissions.data.appLevelRole) {
    
      return userPermissions.data.appLevelRole;
    }
    
    // // If no appLevelRole, check if the data itself is the role data
    
    // Return the data directly if it doesn't have appLevelRole wrapper
    return userPermissions.data;
  }, [userPermissions]);

  // Get user's role name (e.g., "admin", "manager", "user")
  const getUserRole = useCallback((): string | null => {
    const appLevelRole = getAppLevelRole();
    return appLevelRole?.group_name || null;
  }, [getAppLevelRole]);

  // Get user's role ID
  const getUserRoleId = useCallback((): number | null => {
    const appLevelRole = getAppLevelRole();
    return appLevelRole?.app_level_role_id || null;
  }, [getAppLevelRole]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionId: string): boolean => {
    const appLevelRole = getAppLevelRole();
    if (!appLevelRole?.permissions) return false;
    
    // Super admin gets full access to everything
    if (appLevelRole.group_name === 'super_admin' ) {
      return true;
    }
    
    // Get the role-specific permissions (e.g., "admin" permissions)
    const roleName = appLevelRole.group_name;
    const rolePermissions = appLevelRole.permissions[roleName];
    
    if (!Array.isArray(rolePermissions)) return false;
    
    // Find the specific permission and check its value
    const permission = rolePermissions.find((p: any) => p.id === permissionId);
    return permission?.value === true;
  }, [getAppLevelRole]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissionIds: string[]): boolean => {
    if (!permissionIds.length) return false;
    
    // Super admin gets full access to everything
    const appLevelRole = getAppLevelRole();
    if (appLevelRole?.group_name === 'super_admin' ) {
      return true;
    }
    
    return permissionIds.some(permissionId => hasPermission(permissionId));
  }, [hasPermission, getAppLevelRole]);

  // Check if user has all of the specified permissions
  const hasAllPermissions = useCallback((permissionIds: string[]): boolean => {
    if (!permissionIds.length) return false;
    
    // Super admin gets full access to everything
    const appLevelRole = getAppLevelRole();
    if (appLevelRole?.group_name === 'super_admin' ) {
      return true;
    }
    
    return permissionIds.every(permissionId => hasPermission(permissionId));
  }, [hasPermission, getAppLevelRole]);

  // Get all permissions for the user's role
  const getAllPermissions = useCallback(() => {
    const appLevelRole = getAppLevelRole();
    if (!appLevelRole?.permissions) return [];
    
    // Super admin gets all possible permissions
    if (appLevelRole.group_name === 'super_admin' ) {
      return [
        { id: 'manage_admins', name: 'Manage Administrators', value: true },
        { id: 'system_backup', name: 'System Backup & Restore', value: true },
        { id: 'create_projects', name: 'Create Projects', value: true },
        { id: 'delete_projects', name: 'Delete Projects', value: true },
        { id: 'manage_devices', name: 'Manage Devices', value: true },
        { id: 'view_analytics', name: 'View Analytics', value: true },
        { id: 'manage_users', name: 'Manage Users', value: true },
        { id: 'manage_system', name: 'Manage System Settings', value: true },
        { id: 'manage_security', name: 'Manage Security Settings', value: true },
        { id: 'manage_user_access', name: 'User Access Settings', value: true },
        { id: 'view_audit_logs', name: 'View Audit Logs', value: true },
        { id: 'edit_projects', name: 'Edit Projects', value: true },
        { id: 'view_projects', name: 'View All Projects', value: true },
        { id: 'view_devices', name: 'View Devices', value: true },
        { id: 'export_analytics', name: 'Export Analytics', value: true },
        { id: 'assign_roles', name: 'Assign Roles', value: true }
      ];
    }
    
    const roleName = appLevelRole.group_name;
    const rolePermissions = appLevelRole.permissions[roleName];
    
    return Array.isArray(rolePermissions) ? rolePermissions : [];
  }, [getAppLevelRole]);

  // Get active (enabled) permissions count
  const getActivePermissionsCount = useCallback((): number => {
    const permissions = getAllPermissions();
    return permissions.filter((p: any) => p.value === true).length;
  }, [getAllPermissions]);

  // Get total permissions count
  const getTotalPermissionsCount = useCallback((): number => {
    const permissions = getAllPermissions();
    return permissions.length;
  }, [getAllPermissions]);

  // Check if user has a specific role
  const hasRole = useCallback((roleName: string): boolean => {
    const userRole = getUserRole();
    return userRole === roleName;
  }, [getUserRole]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roleNames: string[]): boolean => {
    const userRole = getUserRole();
    return roleNames.includes(userRole || '');
  }, [getUserRole]);

  // Check if user is admin or higher
  const isAdminOrHigher = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin'].includes(userRole || '');
  }, [getUserRole]);

  // Check if user is manager or higher
  const isManagerOrHigher = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin', 'manager'].includes(userRole || '');
  }, [getUserRole]);

  // Check if user is super admin
  const isSuperAdmin = useCallback((): boolean => {
    const userRole = getUserRole();
    return userRole === 'super_admin';
  }, [getUserRole]);

  // Check if user can access admin features
  const canAccessAdminFeatures = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin'].includes(userRole || '');
  }, [getUserRole]);

  // Check if user can access system settings
  const canAccessSystemSettings = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin'].includes(userRole || '');
  }, [getUserRole]);

  // Check if user can manage other users
  const canManageUsers = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin'].includes(userRole || '');
  }, [getUserRole]);

  // Check if user can manage projects
  const canManageProjects = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin', 'manager'].includes(userRole || '');
  }, [getUserRole]);

  // Check if user can view sensitive data
  const canViewSensitiveData = useCallback((): boolean => {
    const userRole = getUserRole();
    return ['super_admin', 'admin'].includes(userRole || '');
  }, [getUserRole]);

  return {
    // State
    userPermissions: userPermissions?.data,
    loading: userPermissions?.loading,
    error: userPermissions?.error,
    
    // Actions
    fetchUserPermissions,
    
    // Core data
    appLevelRole: getAppLevelRole(),
    userRole: getUserRole(),
    userRoleId: getUserRoleId(),
    
    // Permission checking functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdminOrHigher,
    isManagerOrHigher,
    isSuperAdmin,
    canAccessAdminFeatures,
    canAccessSystemSettings,
    canManageUsers,
    canManageProjects,
    canViewSensitiveData,
    
    // Permission data
    allPermissions: getAllPermissions(),
    activePermissionsCount: getActivePermissionsCount(),
    totalPermissionsCount: getTotalPermissionsCount(),
    
    // Utility functions
    getUserRole,
    getUserRoleId,
    getAllPermissions,
    
    // Check if permissions are loaded
    isLoaded: !!userPermissions?.data && !userPermissions?.loading,
    
    // Check if there was an error
    hasError: !!userPermissions?.error
  };
};

export default useUserPermissionsAfterLogin;
